import { useContext, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileSpreadsheet, Search, Check, X, Star } from '@/components/icons';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { Input } from '@/components/form/Input';
import { Badge } from '@/components/ui/Badge';
import { useToast } from '@/components/ui/useToast';
import { useConfirm } from '@/components/ui/useConfirm';
import { AuthContext } from '@/auth/AuthContext';
import { getSettings } from '@/api/admin';
import {
  approveQuote,
  listQuoteApprovals,
  overrideQuote,
  rejectQuote,
  type QuoteApprovalRow,
} from '@/api/quoteApproval';
import { apiError } from '@/lib/apiError';
import { formatINRSymbol } from '@/lib/format';

const DECISION_TONE: Record<QuoteApprovalRow['decision'], 'muted' | 'success' | 'warning' | 'danger'> = {
  Pending: 'muted',
  Approved: 'success',
  Override: 'warning',
  Rejected: 'danger',
};

export function QuoteApprovalPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const confirmDlg = useConfirm();
  const auth = useContext(AuthContext);
  const user = auth?.user;

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [tab, setTab] = useState<'pending' | 'approved'>('pending');
  const [search, setSearch] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);
  const [overrideState, setOverrideState] = useState<Record<number, { quoteItemId: number; reason: string } | null>>({});

  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: getSettings,
    staleTime: 300_000,
  });

  const listQuery = useQuery({
    queryKey: ['quote-approval', 'all'],
    queryFn: () => listQuoteApprovals(undefined, true),
    refetchInterval: 60_000,
  });

  const data = useMemo(() => listQuery.data ?? [], [listQuery.data]);

  const filtered = useMemo(() => {
    const rows = data.filter((row) => {
      // Tab filter
      if (tab === 'pending' && row.decision !== 'Pending') return false;
      if (tab === 'approved' && row.decision === 'Pending') return false;
      if (fromDate && row.decidedAt) {
        if (row.decidedAt.slice(0, 10) < fromDate) return false;
      }
      if (toDate && row.decidedAt) {
        if (row.decidedAt.slice(0, 10) > toDate) return false;
      }
      if (search) {
        const q = search.toLowerCase();
        const hay = [
          row.rfqNo,
          row.itemName,
          row.unit,
          row.decision,
          row.selectedVendor,
          ...row.quotes.map((qt) => qt.vendor),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    rows.sort((a, b) => {
      // Pending always before decided
      const aPending = a.decision === 'Pending';
      const bPending = b.decision === 'Pending';
      if (aPending && !bPending) return -1;
      if (!aPending && bPending) return 1;
      if (aPending && bPending) return b.rfqId - a.rfqId;
      const aDate = a.decidedAt ?? '';
      const bDate = b.decidedAt ?? '';
      return bDate.localeCompare(aDate);
    });
    return rows;
  }, [data, tab, fromDate, toDate, search]);

  // "Actionable" pending = has at least one vendor quote to award. Items with
  // no quotes can't be approved (no vendor/price), so they're tracked
  // separately and don't gate "Approve All".
  const pendingCount = useMemo(
    () => data.filter((r) => r.decision === 'Pending' && r.quotes.length > 0).length,
    [data],
  );
  const awaitingCount = useMemo(
    () => data.filter((r) => r.decision === 'Pending' && r.quotes.length === 0).length,
    [data],
  );

  function invalidate() {
    qc.invalidateQueries({ queryKey: ['quote-approval'] });
    qc.invalidateQueries({ queryKey: ['rfq'] });
  }

  const approveMutation = useMutation({
    mutationFn: ({ rfqItemId, quoteItemId }: { rfqItemId: number; quoteItemId: number }) =>
      approveQuote(rfqItemId, quoteItemId),
    onSuccess: () => {
      invalidate();
      toast.pushStep('Quote approved', {
        body: 'Next step — raise the Purchase Order for the awarded vendor.',
        href: '/purchase/po',
        nextLabel: 'Open Purchase Orders',
        tag: 'quote-approved',
      });
    },
    onError: (e: unknown) => toast.push(apiError(e, 'Approve failed'), 'error'),
  });

  const rejectMutation = useMutation({
    mutationFn: (rfqItemId: number) => rejectQuote(rfqItemId),
    onSuccess: () => { invalidate(); toast.push('Rejected', 'info'); },
    onError: (e: unknown) => toast.push(apiError(e, 'Reject failed'), 'error'),
  });

  const overrideMutation = useMutation({
    mutationFn: ({ rfqItemId, quoteItemId, reason }: { rfqItemId: number; quoteItemId: number; reason: string }) =>
      overrideQuote(rfqItemId, quoteItemId, reason),
    onSuccess: () => {
      invalidate();
      setOverrideState({});
      toast.push('Decision overridden', 'success');
    },
    onError: (e: unknown) => toast.push(apiError(e, 'Override failed'), 'error'),
  });

  async function handleApproveAll() {
    // Only items that actually have vendor quotes can be approved.
    const allPending = data.filter((r) => r.decision === 'Pending' && r.quotes.length > 0);
    const approvable = allPending.filter((r) => r.recommendedQuoteItemId != null);
    const skipped = allPending.length - approvable.length;

    if (approvable.length === 0) {
      toast.push(
        allPending.length === 0
          ? 'No pending items to approve.'
          : `None of the ${allPending.length} pending item(s) have a recommended vendor yet — approve them individually or wait for vendor quotes.`,
        'error',
      );
      return;
    }

    const msg = skipped > 0
      ? `Approve ${approvable.length} item(s) with AI-recommended vendors?\n\n${skipped} item(s) will be skipped — they have no recommendation yet.`
      : `Approve all ${approvable.length} pending item(s) with AI-recommended vendors and generate POs?`;
    if (!await confirmDlg(msg)) return;

    setBulkLoading(true);
    let succeeded = 0;
    let failed = 0;
    try {
      for (const row of approvable) {
        try {
          await approveQuote(row.rfqItemId, row.recommendedQuoteItemId as number);
          succeeded++;
        } catch {
          failed++;
        }
      }
      qc.invalidateQueries({ queryKey: ['quote-approval'] });
      qc.invalidateQueries({ queryKey: ['rfq'] });
      qc.invalidateQueries({ queryKey: ['po'] });
      qc.invalidateQueries({ queryKey: ['pr'] });

      if (failed === 0 && skipped === 0) {
        toast.push(`Approved ${succeeded} item(s)`, 'success');
      } else {
        const parts = [`Approved ${succeeded} item(s)`];
        if (failed > 0) parts.push(`${failed} failed`);
        if (skipped > 0) parts.push(`${skipped} skipped (no recommendation)`);
        toast.push(parts.join(' · '), failed > 0 ? 'error' : 'info');
      }
    } finally {
      setBulkLoading(false);
    }
  }

  // Export helpers (client-side CSV for Excel)
  function exportCSV() {
    const rows = filtered.map((row) => {
      const rec = row.quotes.find((q) => q.quoteItemId === row.recommendedQuoteItemId);
      return [
        row.itemName,
        `${row.qty} ${row.unit}`,
        rec?.vendor ?? '',
        rec ? `₹${rec.unitPrice}` : '',
        rec ? `₹${rec.total}` : '',
        rec?.aiScore?.toFixed(1) ?? '',
        `₹${row.savingsVsHighest}`,
        row.decision,
      ];
    });
    const header = ['Item', 'Qty', 'Recommended Vendor', 'Unit Price', 'Total', 'AI Score', 'Savings vs Highest', 'Decision'];
    const csv = [header, ...rows].map((r) => r.map((c) => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'quote-approval.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  const userInitial = user?.fullName?.[0]?.toUpperCase() ?? 'U';
  const companyName = settings?.name ?? "Gauvin's Home Pvt Ltd";

  return (
    <div>
      <SectionHeader
        title="Quote Approval"      />

      {/* ── Tabs — button-style pill segmented control ── */}
      <div className="mb-2 inline-flex gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] p-1 sm:mb-2">
        {(['pending', 'approved'] as const).map((t) => {
          const count = t === 'pending'
            ? data.filter((r) => r.decision === 'Pending').length
            : data.filter((r) => r.decision !== 'Pending').length;
          const active = tab === t;
          return (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={
                'rounded-md px-3 py-1.5 text-[11px] font-bold transition sm:px-4 sm:py-1.5 sm:text-xs ' +
                (active
                  ? 'bg-accent text-white shadow-sm'
                  : 'text-[var(--text-muted)] hover:bg-[var(--bg-soft)] hover:text-[var(--text-primary)]')
              }
            >
              {t === 'pending' ? `Pending Approval (${count})` : `Approved / History (${count})`}
            </button>
          );
        })}
      </div>

      <FilterBar>
        {/* Row 1 on mobile: Search stretches full width. Row 2 packs From +
            To + Excel (+ Approve All if pending) into a single line. */}
        <FilterField label="Search" className="col-span-3">
          <div className="relative w-full">
            <Search
              size={11}
              className="pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] sm:left-2.5"
              strokeWidth={2.25}
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search"
              className="!pl-5 sm:!pl-8 sm:min-w-[200px]"
              aria-label="Search items or vendors"
            />
          </div>
        </FilterField>
        <FilterField label="From">
          <Input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="!px-1 sm:!px-2.5"
          />
        </FilterField>
        <FilterField label="To">
          <Input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="!px-1 sm:!px-2.5"
          />
        </FilterField>
        {/* Action chips live inside the filter block on both viewports.
            Mobile: chips share the second row with the date fields.
            Desktop: right-pinned via ml-auto. */}
        {tab === 'pending' && pendingCount > 0 && (
          <button
            type="button"
            onClick={handleApproveAll}
            disabled={bulkLoading}
            className="inline-flex h-7 items-center justify-center gap-1 rounded-lg bg-success px-2 text-[11px] font-bold text-white shadow-sm transition hover:brightness-110 disabled:opacity-60 sm:ml-auto sm:h-8 sm:self-end sm:gap-1.5 sm:px-3 sm:text-xs"
          >
            {bulkLoading ? 'Approving…' : <><Check size={14} className="mr-1 inline-block align-[-2px]" /> Approve All ({pendingCount})</>}
          </button>
        )}
        <button
          type="button"
          onClick={exportCSV}
          className="inline-flex h-7 items-center justify-center gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] px-2 text-[11px] font-bold text-[var(--text-secondary)] shadow-sm transition hover:border-accent hover:text-accent sm:ml-auto sm:h-8 sm:self-end sm:gap-1.5 sm:px-3 sm:text-xs"
        >
          <FileSpreadsheet size={13} /> Excel
        </button>
      </FilterBar>

      {/* ── Approver card (compact on mobile, full on desktop) ── */}
      {user && (
        <div className="mb-2 flex items-center justify-between rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] px-2.5 py-2 sm:mb-2 sm:px-5 sm:py-4">
          <div className="flex min-w-0 items-center gap-2 sm:gap-4">
            <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-accent text-xs font-black text-white sm:h-12 sm:w-12 sm:text-xl">
              {userInitial}
            </div>
            <div className="min-w-0">
              <div className="truncate text-[12px] font-extrabold text-[var(--text-primary)] sm:text-base">{user.fullName}</div>
              <div className="hidden text-xs text-[var(--text-muted)] sm:block">
                {user.department ?? user.roleCode} — {companyName}
              </div>
              <div className="text-[10px] font-bold text-accent sm:mt-0.5 sm:text-xs">Authorized Purchase Approver</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] text-[var(--text-muted)] sm:text-xs">Pending</div>
            <div className="text-lg font-black text-accent sm:text-3xl">{pendingCount}</div>
            {awaitingCount > 0 && (
              <div className="text-[9px] font-bold text-warning sm:text-[11px]">{awaitingCount} awaiting quotes</div>
            )}
          </div>
        </div>
      )}

      {/* ── Quote cards ── */}
      <div className="space-y-3 sm:space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-[12px] font-extrabold text-[var(--text-primary)] sm:text-sm">
            {tab === 'pending' ? 'Pending Approvals' : 'Approved / History'}
          </span>
          <span className="text-[10px] text-[var(--text-muted)] sm:text-xs">Showing {filtered.length} of {data.length} items</span>
        </div>

        {listQuery.isFetching && data.length === 0 ? (
          <div className="rounded-card border border-[var(--border-color)] p-10 text-center text-sm text-[var(--text-muted)]">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="rounded-card border border-[var(--border-color)] p-10 text-center text-sm text-[var(--text-muted)]">No items found</div>
        ) : (
          filtered.map((row) => {
            const canApprove = row.decision === 'Pending' || row.decision === 'Rejected';
            const isDecided = row.decision === 'Approved' || row.decision === 'Override';
            return (
              <div key={row.rfqItemId} className="rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] overflow-hidden">

                {/* Card header — stacked on mobile, inline on desktop */}
                <div className="border-b border-[var(--border-color)] bg-accent/[0.04] px-2.5 py-2 sm:px-5 sm:py-3">
                  <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                    <div className="flex min-w-0 flex-1 flex-col gap-1 sm:flex-row sm:items-center sm:gap-2 sm:flex-wrap">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-[13px] font-extrabold text-[var(--text-primary)] sm:text-base">{row.itemName}</span>
                        <Badge tone={DECISION_TONE[row.decision]}>{row.decision}</Badge>
                        {row.decision === 'Pending' && row.quotes.length === 0 && (
                          <span className="rounded bg-warning/15 px-1.5 py-0.5 text-[9px] font-bold text-warning sm:text-[10px]">Awaiting quotes</span>
                        )}
                      </div>
                      <span className="text-[10px] text-[var(--text-muted)] sm:text-xs">
                        {String(row.qty)} {row.unit}
                        {' · '}<span className="font-semibold text-[var(--text-secondary)]">{row.rfqNo}</span>
                        {row.targetPrice ? ` · Target: ₹${row.targetPrice}` : ''}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-2 sm:justify-end sm:gap-3 sm:shrink-0">
                      <span className="text-[10px] font-bold text-success sm:text-xs">Savings: {formatINRSymbol(row.savingsVsHighest)}</span>
                      {row.decidedAt && (
                        <span className="text-[10px] text-[var(--text-muted)] sm:text-xs">
                          {new Date(row.decidedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </span>
                      )}
                      {canApprove && (
                        <button
                          disabled={rejectMutation.isPending}
                          onClick={() => rejectMutation.mutate(row.rfqItemId)}
                          className="rounded-lg bg-danger/10 px-2 py-0.5 text-[11px] font-bold text-danger hover:bg-danger/20 transition-colors disabled:opacity-50 sm:px-3 sm:py-1 sm:text-xs"
                        >
                          <X size={13} className="mr-1 inline-block align-[-2px]" /> Reject
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Vendor list — mobile stacked cards */}
                <div className="sm:hidden">
                  {row.quotes.length === 0 && (
                    <div className="border-t border-[var(--border-color)] px-2.5 py-3 text-[11px] leading-relaxed text-[var(--text-muted)]">
                      No quotes received yet. Record a vendor quote from Quotations / RFQ, or reject this item.
                    </div>
                  )}
                  {row.quotes.map((q) => {
                    const isRecommended = q.quoteItemId === row.recommendedQuoteItemId;
                    const isSelected = q.quoteItemId === row.selectedQuoteItemId;
                    const isOverriding = overrideState[row.rfqItemId]?.quoteItemId === q.quoteItemId;
                    return (
                      <div
                        key={q.quoteItemId}
                        className={`border-t border-[var(--border-color)] px-2.5 py-2 ${
                          isSelected ? 'bg-success/[0.06]' : isRecommended ? 'bg-accent/[0.03]' : ''
                        }`}
                      >
                        <div className="mb-1.5 flex items-center gap-1.5 flex-wrap">
                          <span className="text-[12px] font-bold text-[var(--text-primary)]">{q.vendor}</span>
                          {isRecommended && (
                            <span className="rounded bg-accent/20 px-1.5 py-0.5 text-[9px] font-bold text-accent"><Star size={11} className="mr-1 inline-block align-[-1px]" /> AI Pick</span>
                          )}
                          {isSelected && (
                            <span className="rounded bg-success/20 px-1.5 py-0.5 text-[9px] font-bold text-success"><Check size={11} className="mr-1 inline-block align-[-1px]" /> Selected</span>
                          )}
                        </div>
                        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-[11px]">
                          <dt className="text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">Delivery</dt>
                          <dd className="text-[var(--text-primary)]">{q.deliveryDays != null ? `${q.deliveryDays}d` : '—'}</dd>
                          <dt className="text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">Unit Price</dt>
                          <dd className="text-[var(--text-primary)]">{formatINRSymbol(q.unitPrice)}</dd>
                          <dt className="text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">Total</dt>
                          <dd className="font-bold text-[var(--text-primary)]">{formatINRSymbol(q.total)}</dd>
                          <dt className="text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">AI Score</dt>
                          <dd>
                            {q.aiScore != null ? (
                              <span className="font-bold text-success">{q.aiScore.toFixed(1)}</span>
                            ) : (
                              <span className="text-[var(--text-muted)]">—</span>
                            )}
                          </dd>
                        </dl>
                        {/* Action — full width on mobile */}
                        <div className="mt-2 [&_button]:!w-full">
                          {canApprove && (
                            <button
                              disabled={approveMutation.isPending}
                              onClick={() => approveMutation.mutate({ rfqItemId: row.rfqItemId, quoteItemId: q.quoteItemId })}
                              className="rounded-lg bg-success/10 px-3 py-1.5 text-xs font-bold text-success hover:bg-success/20 transition-colors disabled:opacity-50"
                            >
                              <Check size={13} className="mr-1 inline-block align-[-2px]" /> Approve
                            </button>
                          )}
                          {isDecided && isSelected && (
                            <button disabled className="rounded-lg bg-success/10 px-3 py-1.5 text-xs font-bold text-success cursor-default opacity-60">
                              <Check size={13} className="mr-1 inline-block align-[-2px]" /> Approved
                            </button>
                          )}
                          {isDecided && !isSelected && !isOverriding && (
                            <button
                              onClick={() =>
                                setOverrideState((s) => ({
                                  ...s,
                                  [row.rfqItemId]: { quoteItemId: q.quoteItemId, reason: '' },
                                }))
                              }
                              className="rounded-lg bg-warning/10 px-3 py-1.5 text-xs font-bold text-warning hover:bg-warning/20 transition-colors"
                            >
                              ↺ Override
                            </button>
                          )}
                          {isOverriding && (
                            <div className="flex flex-col gap-1.5">
                              <input
                                autoFocus
                                type="text"
                                placeholder="Override reason…"
                                value={overrideState[row.rfqItemId]?.reason ?? ''}
                                onChange={(e) =>
                                  setOverrideState((s) => ({
                                    ...s,
                                    [row.rfqItemId]: { quoteItemId: q.quoteItemId, reason: e.target.value },
                                  }))
                                }
                                className="!w-full rounded border border-[var(--border-color)] bg-[var(--bg-secondary)] px-2 py-1 text-xs outline-none focus:border-accent text-[var(--text-primary)]"
                              />
                              <div className="flex gap-1.5 [&_button]:!w-auto">
                                <button
                                  disabled={!overrideState[row.rfqItemId]?.reason?.trim() || overrideMutation.isPending}
                                  onClick={() => {
                                    const reason = overrideState[row.rfqItemId]?.reason?.trim();
                                    if (!reason) return;
                                    overrideMutation.mutate({ rfqItemId: row.rfqItemId, quoteItemId: q.quoteItemId, reason });
                                  }}
                                  className="flex-1 rounded bg-warning/20 px-2.5 py-1 text-xs font-bold text-warning hover:bg-warning/30 disabled:opacity-50 transition-colors"
                                >
                                  Confirm
                                </button>
                                <button
                                  onClick={() => setOverrideState((s) => ({ ...s, [row.rfqItemId]: null }))}
                                  className="flex-1 rounded bg-white/[0.06] px-2.5 py-1 text-xs text-[var(--text-muted)] hover:bg-white/10 transition-colors"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Vendor table — desktop */}
                <table className="hidden w-full text-sm sm:table">
                  <colgroup>
                    <col />
                    <col style={{ width: '80px' }} />
                    <col style={{ width: '110px' }} />
                    <col style={{ width: '110px' }} />
                    <col style={{ width: '90px' }} />
                    <col style={{ width: '220px' }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th className="border-b border-[var(--border-color)] bg-[var(--bg-secondary)] px-5 py-1.5 text-left align-middle text-[10px] font-black uppercase tracking-wide text-accent">Vendor</th>
                      <th className="border-b border-[var(--border-color)] bg-[var(--bg-secondary)] px-3.5 py-1.5 text-center text-[10px] font-black uppercase tracking-wide text-accent">Delivery</th>
                      <th className="border-b border-[var(--border-color)] bg-[var(--bg-secondary)] px-3.5 py-1.5 text-center text-[10px] font-black uppercase tracking-wide text-accent">Unit Price</th>
                      <th className="border-b border-[var(--border-color)] bg-[var(--bg-secondary)] px-3.5 py-1.5 text-center text-[10px] font-black uppercase tracking-wide text-accent">Total</th>
                      <th className="border-b border-[var(--border-color)] bg-[var(--bg-secondary)] px-3.5 py-1.5 text-center text-[10px] font-black uppercase tracking-wide text-accent">AI Score</th>
                      <th className="border-b border-[var(--border-color)] bg-[var(--bg-secondary)] px-3.5 py-1.5 text-center text-[10px] font-black uppercase tracking-wide text-accent">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--border-color)]">
                    {row.quotes.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-5 py-4 text-center text-xs text-[var(--text-muted)]">
                          No quotes received yet — record a vendor quote from Quotations / RFQ, or reject this item.
                        </td>
                      </tr>
                    )}
                    {row.quotes.map((q) => {
                      const isRecommended = q.quoteItemId === row.recommendedQuoteItemId;
                      const isSelected = q.quoteItemId === row.selectedQuoteItemId;
                      const isOverriding = overrideState[row.rfqItemId]?.quoteItemId === q.quoteItemId;
                      return (
                        <tr
                          key={q.quoteItemId}
                          className={`transition-colors ${
                            isSelected ? 'bg-success/[0.06]' : isRecommended ? 'bg-accent/[0.03]' : ''
                          }`}
                        >
                          {/* Vendor */}
                          <td className="px-5 py-1.5 align-middle">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="font-bold text-[var(--text-primary)]">{q.vendor}</span>
                              {isRecommended && (
                                <span className="rounded bg-accent/20 px-1.5 py-0.5 text-[10px] font-bold text-accent"><Star size={11} className="mr-1 inline-block align-[-1px]" /> AI Pick</span>
                              )}
                              {isSelected && (
                                <span className="rounded bg-success/20 px-1.5 py-0.5 text-[10px] font-bold text-success"><Check size={11} className="mr-1 inline-block align-[-1px]" /> Selected</span>
                              )}
                            </div>
                          </td>
                          {/* Delivery */}
                          <td className="px-3.5 py-1.5 text-center text-sm text-[var(--text-muted)]">
                            {q.deliveryDays != null ? `${q.deliveryDays}d` : '—'}
                          </td>
                          {/* Unit Price */}
                          <td className="px-3.5 py-1.5 text-center font-normal text-[var(--text-primary)]">
                            {formatINRSymbol(q.unitPrice)}
                          </td>
                          {/* Total */}
                          <td className="px-3.5 py-1.5 text-center font-normal text-[var(--text-primary)]">
                            {formatINRSymbol(q.total)}
                          </td>
                          {/* AI Score */}
                          <td className="px-3.5 py-1.5 text-center">
                            {q.aiScore != null ? (
                              <span className="font-normal text-success">{q.aiScore.toFixed(1)}</span>
                            ) : (
                              <span className="text-[var(--text-muted)]">—</span>
                            )}
                          </td>
                          {/* Action */}
                          <td className="px-3.5 py-1.5 text-center">
                            {canApprove && (
                              <button
                                disabled={approveMutation.isPending}
                                onClick={() => approveMutation.mutate({ rfqItemId: row.rfqItemId, quoteItemId: q.quoteItemId })}
                                className="rounded-lg bg-success/10 px-3 py-1 text-xs font-bold text-success hover:bg-success/20 transition-colors disabled:opacity-50"
                              >
                                <Check size={13} className="mr-1 inline-block align-[-2px]" /> Approve
                              </button>
                            )}
                            {isDecided && isSelected && (
                              <div className="inline-flex items-center gap-1.5">
                                <button disabled className="rounded-lg bg-success/10 px-3 py-1 text-xs font-bold text-success cursor-default opacity-60">
                                  <Check size={13} className="mr-1 inline-block align-[-2px]" /> Approved
                                </button>
                              </div>
                            )}
                            {isDecided && !isSelected && !isOverriding && (
                              <button
                                onClick={() =>
                                  setOverrideState((s) => ({
                                    ...s,
                                    [row.rfqItemId]: { quoteItemId: q.quoteItemId, reason: '' },
                                  }))
                                }
                                className="rounded-lg bg-warning/10 px-3 py-1 text-xs font-bold text-warning hover:bg-warning/20 transition-colors"
                              >
                                ↺ Override
                              </button>
                            )}
                            {isOverriding && (
                              <div className="flex flex-col gap-1.5 items-end">
                                <input
                                  autoFocus
                                  type="text"
                                  placeholder="Override reason…"
                                  value={overrideState[row.rfqItemId]?.reason ?? ''}
                                  onChange={(e) =>
                                    setOverrideState((s) => ({
                                      ...s,
                                      [row.rfqItemId]: { quoteItemId: q.quoteItemId, reason: e.target.value },
                                    }))
                                  }
                                  className="w-40 rounded border border-[var(--border-color)] bg-[var(--bg-secondary)] px-2 py-1 text-xs outline-none focus:border-accent text-[var(--text-primary)]"
                                />
                                <div className="flex gap-1.5">
                                  <button
                                    disabled={!overrideState[row.rfqItemId]?.reason?.trim() || overrideMutation.isPending}
                                    onClick={() => {
                                      const reason = overrideState[row.rfqItemId]?.reason?.trim();
                                      if (!reason) return;
                                      overrideMutation.mutate({ rfqItemId: row.rfqItemId, quoteItemId: q.quoteItemId, reason });
                                    }}
                                    className="rounded bg-warning/20 px-2.5 py-1 text-xs font-bold text-warning hover:bg-warning/30 disabled:opacity-50 transition-colors"
                                  >
                                    Confirm
                                  </button>
                                  <button
                                    onClick={() => setOverrideState((s) => ({ ...s, [row.rfqItemId]: null }))}
                                    className="rounded bg-white/[0.06] px-2.5 py-1 text-xs text-[var(--text-muted)] hover:bg-white/10 transition-colors"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          }))}
      </div>
    </div>
  );
}
